import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const QuestionnaireList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [userStatus, setUserStatus] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          loadQuestionnaires(),
          loadUserStatus()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const loadQuestionnaires = async () => {
    try {
      const response = await api.get('/questionnaire/types');
      const items = Array.isArray(response.data.questionnaires)
        ? response.data.questionnaires
        : Array.isArray(response.data.types)
          ? response.data.types
          : [];

      setQuestionnaires(items.map((item) => ({
        type: item.id || item.type,
        title: item.title,
        description: item.description,
        page: item.pages || item.page || 1
      })));
    } catch (error) {
      console.error('Failed to load questionnaires:', error);
      toast.error('Failed to load questionnaires');
    }
  };

  const loadUserStatus = async () => {
    try {
      const response = await api.get('/questionnaire/history/all?limit=50');
      setUserStatus({
        completed: Array.isArray(response.data.data) ? response.data.data : [],
        inProgress: []
      });
    } catch (error) {
      console.error('Failed to load user status:', error);
      setUserStatus({ completed: [], inProgress: [] });
    }
  };

  const getStatusForQuestionnaire = (type) => {
    if (!userStatus) return 'available';
    
    const completed = userStatus.completed.find(q => q.questionnaire_type === type);
    if (completed) return 'completed';
    
    const inProgress = userStatus.inProgress.find(q => q.questionnaire_type === type);
    if (inProgress) return 'in_progress';
    
    return 'available';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Available';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'in_progress':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const handleQuestionnaireClick = (type) => {
    navigate(`/questionnaire/${type}`);
  };

  const getCompletionDate = (type) => {
    const completed = userStatus?.completed.find(q => q.questionnaire_type === type);
    if (completed) {
      return new Date(completed.completed_at).toLocaleDateString();
    }
    return null;
  };

  const getHealthScore = (type) => {
    const completed = userStatus?.completed.find(q => q.questionnaire_type === type);
    if (completed && completed.health_score) {
      const score = parseFloat(completed.health_score);
      return !isNaN(score) ? score.toFixed(1) : completed.health_score;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Health Questionnaires</h1>
        <p className="text-gray-600">
          Complete comprehensive health assessments to get personalized insights and predictions.
        </p>
      </div>

      {/* Show comprehensive assessment only if NOT completed */}
      {!loading && questionnaires.length === 1 && questionnaires[0].type === 'comprehensive' && getStatusForQuestionnaire('comprehensive') !== 'completed' && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-8 mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Comprehensive Health Assessment
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Take our complete health assessment to get personalized insights and predictions for your brain, liver, lung, and stomach health. This evaluation takes about 10-15 minutes.
            </p>
            <button
              onClick={() => navigate('/questionnaire/comprehensive')}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
            >
              Start Assessment
            </button>
          </div>
        </div>
      )}

      {/* Show completed message when comprehensive assessment is done */}
      {!loading && questionnaires.length === 1 && questionnaires[0].type === 'comprehensive' && getStatusForQuestionnaire('comprehensive') === 'completed' && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-8 mb-8">
          <div className="text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Assessment Completed!
            </h2>
            <p className="text-gray-600 mb-2 max-w-2xl mx-auto">
              You have successfully completed the Comprehensive Health Assessment.
            </p>
            <p className="text-green-600 font-medium">
              No pending assignments. Great job! 🎉
            </p>
            <button
              onClick={() => navigate('/organ-health')}
              className="mt-6 bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium text-lg"
            >
              View Results
            </button>
          </div>
        </div>
      )}

      {questionnaires.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md mx-auto">
            <svg className="w-16 h-16 text-yellow-400 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Questionnaires Available</h3>
            <p className="text-yellow-700">
              Health assessments are currently being loaded. Please check back later.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireList;
